import * as THREE from 'three';

/**
 * Calculate the Euclidean distance between two 3D points
 * @param {Array} point1 - First point [x, y, z]
 * @param {Array} point2 - Second point [x, y, z]
 * @returns {number} Distance in meters
 */
export function calculateDistance3D(point1, point2) {
  if (!point1 || !point2 || point1.length < 3 || point2.length < 3) {
    return 0;
  }

  const dx = point2[0] - point1[0];
  const dy = point2[1] - point1[1];
  const dz = point2[2] - point1[2];

  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate the area of a polygon defined by 3D points
 * Uses the Shoelace formula projected onto the XY plane
 * @param {Array} points - Array of points [[x1,y1,z1], [x2,y2,z2], ...]
 * @returns {number} Area in square meters
 */
export function calculatePolygonArea(points) {
  if (!points || points.length < 3) {
    return 0;
  }

  // Close the polygon if not already closed
  const closedPoints = [...points];
  if (closedPoints[0] !== closedPoints[closedPoints.length - 1]) {
    closedPoints.push(closedPoints[0]);
  }

  let area = 0;
  for (let i = 0; i < closedPoints.length - 1; i++) {
    area += closedPoints[i][0] * closedPoints[i + 1][1];
    area -= closedPoints[i + 1][0] * closedPoints[i][1];
  }

  return Math.abs(area / 2);
}

/**
 * Calculate the volume of a prism with a polygon base and given height
 * @param {Array} basePoints - Array of points defining the base polygon
 * @param {number} height - Height of the prism
 * @returns {number} Volume in cubic meters
 */
export function calculateVolume(basePoints, height) {
  const baseArea = calculatePolygonArea(basePoints);
  return baseArea * height;
}

/**
 * Format a distance value for display
 * @param {number} distance - Distance in meters
 * @returns {string} Formatted string with appropriate units
 */
export function formatDistance(distance) {
  if (distance < 0.01) {
    return `${(distance * 1000).toFixed(1)} mm`;
  } else if (distance < 1) {
    return `${(distance * 100).toFixed(1)} cm`;
  } else if (distance < 1000) {
    return `${distance.toFixed(2)} m`;
  } else {
    return `${(distance / 1000).toFixed(2)} km`;
  }
}

/**
 * Format an area value for display
 * @param {number} area - Area in square meters
 * @returns {string} Formatted string with appropriate units
 */
export function formatArea(area) {
  if (area < 0.01) {
    return `${(area * 10000).toFixed(1)} cm²`;
  } else if (area < 10000) {
    return `${area.toFixed(2)} m²`;
  } else {
    return `${(area / 10000).toFixed(2)} ha`;
  }
}

/**
 * Format a volume value for display
 * @param {number} volume - Volume in cubic meters
 * @returns {string} Formatted string with appropriate units
 */
export function formatVolume(volume) {
  if (volume < 0.001) {
    return `${(volume * 1000000).toFixed(1)} cm³`;
  } else if (volume < 1) {
    return `${(volume * 1000).toFixed(1)} L`;
  } else {
    return `${volume.toFixed(2)} m³`;
  }
}

/**
 * Calculate the perpendicular distance from a point to a line
 * @param {Array} point - The point [x, y, z]
 * @param {Array} lineStart - Start point of the line [x, y, z]
 * @param {Array} lineEnd - End point of the line [x, y, z]
 * @returns {number} Perpendicular distance
 */
export function pointToLineDistance(point, lineStart, lineEnd) {
  const line = new THREE.Vector3(
    lineEnd[0] - lineStart[0],
    lineEnd[1] - lineStart[1],
    lineEnd[2] - lineStart[2]
  );

  const pointVec = new THREE.Vector3(
    point[0] - lineStart[0],
    point[1] - lineStart[1],
    point[2] - lineStart[2]
  );

  const lineLength = line.length();
  if (lineLength === 0) return pointVec.length();

  const t = Math.max(0, Math.min(1, pointVec.dot(line) / (lineLength * lineLength)));
  const projection = line.multiplyScalar(t);

  return pointVec.sub(projection).length();
}

/**
 * Get the midpoint between two 3D points
 * @param {Array} point1 - First point [x, y, z]
 * @param {Array} point2 - Second point [x, y, z]
 * @returns {Array} Midpoint [x, y, z]
 */
export function getMidpoint(point1, point2) {
  return [
    (point1[0] + point2[0]) / 2,
    (point1[1] + point2[1]) / 2,
    (point1[2] + point2[2]) / 2
  ];
}

/**
 * Calculate the angle between three points
 * @param {Array} point1 - First point [x, y, z]
 * @param {Array} vertex - Vertex point [x, y, z]
 * @param {Array} point2 - Second point [x, y, z]
 * @returns {number} Angle in degrees
 */
export function calculateAngle(point1, vertex, point2) {
  const v1 = new THREE.Vector3(
    point1[0] - vertex[0],
    point1[1] - vertex[1],
    point1[2] - vertex[2]
  );

  const v2 = new THREE.Vector3(
    point2[0] - vertex[0],
    point2[1] - vertex[1],
    point2[2] - vertex[2]
  );

  const angle = v1.angleTo(v2);
  return (angle * 180) / Math.PI;
}